import { skipToken, useQuery } from '@tanstack/react-query';
import { pmoFlowApi } from '../lib/api/pmo-flow.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  PmoFlowSummaryParams,
  PmoFlowTrendParams,
  PmoScaleExcludeMode,
  PmoScaleScopeKey,
  PmoScaleTailPercentile,
  PmoSupplyRateStage,
} from '../types/pmo-flow.js';

/**
 * PMO フェーズリードタイムのサマリー。区間別の所要日数分位数と集計母数の品質を取得する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムのサマリー取得フック
 * @graph-connects /kpi/pmo-flow/summary [calls] フェーズ区間別リードタイムサマリー取得
 */
export function usePmoFlowSummary(params: PmoFlowSummaryParams) {
  return useQuery({
    queryKey: queryKeys.pmoFlow.summary(params),
    queryFn: () => pmoFlowApi.getSummary(params),
  });
}

/**
 * PMO フェーズリードタイムの月次コホートトレンド。指定区間の p50 / p90 推移を取得する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムの区間別月次トレンド取得フック
 * @graph-connects /kpi/pmo-flow/trend [calls] フェーズ区間別月次コホートトレンド取得
 */
export function usePmoFlowTrend(params: PmoFlowTrendParams) {
  return useQuery({
    queryKey: queryKeys.pmoFlow.trend(params),
    queryFn: () => pmoFlowApi.getTrend(params),
  });
}

/**
 * PMO 充足率。アサイン可能まで仕上がった案件の pt 在庫が、先 2 週間分の予算
 * （月をまたぐ場合は翌月予算から按分）に対してどれだけ積み上がっているかを取得する。
 * 全社横断・期間セレクト非連動のためパラメータは持たない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO充足率（アサイン可能な案件・バグのpt在庫 / 先2週間分の予算）取得フック
 * @graph-connects /kpi/pmo-flow/supply-rate [calls] アサイン可能な案件・バグの充足率取得
 */
export function usePmoSupplyRate() {
  return useQuery({
    queryKey: queryKeys.pmoFlow.supplyRate(),
    queryFn: () => pmoFlowApi.getSupplyRate(),
  });
}

/**
 * 充足率カードの案件・バグドリルダウン。段ごとに取得し、モーダルを開いたときだけ走る
 * （`enabled` が false の間はリクエストしない）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO充足率の案件・バグ明細取得フック。段（アサイン可能/1つ手前）を指定し、モーダル表示時のみ取得する。アサイン可能段は案件とバグ（見積あり・DLE Reviewed済み・未アサイン）を含む
 * @graph-connects /kpi/pmo-flow/supply-rate/projects [calls] 指定した段の案件・バグ明細一覧取得
 */
export function usePmoSupplyRateProjects(stage: PmoSupplyRateStage, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.pmoFlow.supplyRateProjects(stage),
    queryFn: () => pmoFlowApi.getSupplyRateProjects(stage),
    enabled,
  });
}

/**
 * 案件規模別リードタイム。指定したリリース完了月にリリースされた母票の 開始日→リリース完了日 を
 * 実績pt の規模帯ごとに p50 / p90 で取得する。
 *
 * `month` が null なら月を送らず、サーバー側の既定（最新月）に委ねる。初回描画のためだけに
 * クライアントで「今月」を計算すると、月替わりの日にサーバーとずれた月を要求してしまう。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO案件規模別リードタイム（指定リリース完了月の実績pt帯ごとの開始日→リリース完了日 p50/p90）取得フック
 * @graph-connects /kpi/pmo-flow/scale-lead-time [calls] 案件規模別リードタイム取得
 */
export function usePmoScaleLeadTime(month: string | null, exclude: PmoScaleExcludeMode) {
  return useQuery({
    queryKey: queryKeys.pmoFlow.scaleLeadTime(month, exclude),
    queryFn: () => pmoFlowApi.getScaleLeadTime(month, exclude),
  });
}

/**
 * 規模帯（または全体）の尾側（指定分位以上）の案件明細。ドリルダウンを開いたときだけ走る
 * （`enabled` が false の間はリクエストしない）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO案件規模帯（または全体）の指定分位（p75/p90）以上に落ちた案件明細の取得フック。モーダル表示時のみ取得する
 * @graph-connects /kpi/pmo-flow/scale-lead-time/projects [calls] 規模帯の指定分位以上の案件明細取得
 */
export function usePmoScaleTailProjects(
  scope: PmoScaleScopeKey | null,
  month: string,
  percentile: PmoScaleTailPercentile,
  exclude: PmoScaleExcludeMode,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.pmoFlow.scaleTailProjects(scope ?? '', month, percentile, exclude),
    // scope 未選択の間は skipToken でクエリごと止める。こうすると queryFn 内で scope が
    // 非 null に絞り込まれ、キャストも到達不能な防御分岐も要らない
    queryFn:
      scope === null
        ? skipToken
        : () => pmoFlowApi.getScaleTailProjects(scope, month, percentile, exclude),
    enabled,
  });
}
